import {
	getRectBounds,
	isVec2InRange,
	rectClamp,
	rectEquals,
	rectOffset,
	rectPadding,
} from "@/utils/math"
import { vec2 } from "gl-matrix"
import { Rectangle } from "../types"

export const BrushMaxTextureSize = 2048 // 1024
export type BasicCursorName = "brush" | "erase"
export type HealingCursorName = "healing"
export type BrushCursorName = BasicCursorName | HealingCursorName

export interface BrushStroke {
	points: number[]
	size: number
	spacing: number
	flow: number
	feather: number
	density: number
	isErasing: boolean
	isColorAware: boolean
}

export interface BrushCursor {
	name: BrushCursorName
	x: number
	y: number
	feather: number
	radius: number
	flow: number
	density: number
	isErasing: boolean
	isColorAware: boolean
}

export const createEmptyBrushStroke = (cursor: BrushCursor, scale: number): BrushStroke => {
	return {
		points: [],
		spacing: 0.75,
		size: cursor.radius / scale,
		feather: cursor.feather,
		isErasing: cursor.isErasing,
		flow: cursor.flow,
		density: cursor.density,
		isColorAware: cursor.isColorAware,
	}
}

export const computeBrushPointsDistance = (x1: number, y1: number, x2: number, y2: number) => {
	const x = x2 - x1
	const y = y2 - y1
	return Math.hypot(x, y)
}

export function computeBrushTextureSize(width: number, height: number): vec2 {
	const scale = BrushMaxTextureSize / Math.max(width, height)
	width = Math.floor(width * scale)
	height = Math.floor(height * scale)

	return [width, height]
}

export function lerpBrushPoints(a: vec2, b: vec2, t: number): vec2 {
	const out: vec2 = [0, 0]

	const ax = a[0]
	const ay = a[1]

	out[0] = ax + t * (b[0] - ax)
	out[1] = ay + t * (b[1] - ay)

	return out
}

export const addPointToBrushStroke = (
	stroke: BrushStroke,
	cursor: BrushCursor,
	boundaries: vec2,
	forHealing: boolean = false,
): BrushStroke => {
	const brushSize = stroke.size * Math.min(boundaries[0], boundaries[1])

	const featherSpacingScale = forHealing ? 0.5 : 1.0
	const brushSpacing =
		(1.0 / Math.min(boundaries[0], boundaries[1])) *
		brushSize *
		0.5 *
		Math.max(0.02, cursor.feather * featherSpacingScale) *
		stroke.spacing

	if (stroke.points.length === 0) {
		stroke.points.push(cursor.x, cursor.y)
	} else {
		const [lastX, lastY] = stroke.points.slice(-2)

		if (!lastX || !lastY) {
			return stroke
		}

		const distance = computeBrushPointsDistance(lastX, lastY, cursor.x, cursor.y)

		if (distance >= brushSpacing) {
			for (let i = brushSpacing; i < distance; i += brushSpacing) {
				stroke.points.push(
					...lerpBrushPoints([lastX, lastY], [cursor.x, cursor.y], i / distance),
				)
			}
		}
	}

	return stroke
}

export const adjustBrushSpacingByFeather = (
	stroke: BrushStroke,
	boundaries: vec2,
	forHealing: boolean = true,
): BrushStroke => {
	if (stroke.points.length <= 4) return stroke

	const brushSize = stroke.size * Math.min(boundaries[0], boundaries[1])
	const featherSpacingScale = forHealing ? 0.5 : 1.0

	const brushSpacing =
		(1.0 / Math.min(boundaries[0], boundaries[1])) *
		brushSize *
		0.5 *
		Math.max(0.02, stroke.feather * featherSpacingScale) *
		stroke.spacing

	const [firstX, firstY] = stroke.points.slice(0, 2)
	const [lastX, lastY] = stroke.points.slice(-2)
	const [secondX, secondY] = stroke.points.slice(2, 4)

	if (!firstX || !firstY || !lastX || !lastY || !secondX || !secondY) {
		return stroke
	}

	const oldDistance = computeBrushPointsDistance(firstX, firstY, secondX, secondY)

	// when increase feather, new points need have larger brush spacing and therefore less count
	if (oldDistance < brushSpacing) {
		const newPoints = [firstX, firstY]

		const oldCount = stroke.points.length / 2
		const newPointsCount = Math.round(((oldCount - 1) * oldDistance) / brushSpacing + 1)
		const indexDelta = (oldCount - 1) / (newPointsCount - 1)

		for (let j = 1; j < newPointsCount; j++) {
			const index0 = Math.floor(j * indexDelta)
			const x0 = stroke.points[index0 * 2] ?? firstX
			const y0 = stroke.points[index0 * 2 + 1] ?? firstY

			const index1 = Math.ceil(j * indexDelta)
			const x1 = stroke.points[index1 * 2] ?? lastX
			const y1 = stroke.points[index1 * 2 + 1] ?? lastY

			const delta = j * indexDelta - index0
			const [newX, newY] = lerpBrushPoints([x0, y0], [x1, y1], delta)

			newPoints.push(...[newX, newY])
		}

		return { ...stroke, points: newPoints }
	}

	return stroke
}

function getBounds(points: number[]): [vec2, vec2] {
	// TODO: update the type of stroke.points to vec2[]
	const count = 0 === points.length % 2 ? points.length : points.length - 1
	const vertices: vec2[] = []
	for (let i = 0; i < count; i += 2) {
		const x = points[i] ?? 0
		const y = points[i + 1] ?? 0
		vertices.push([x, y])
	}
	return getRectBounds(vertices)
}

function calcBoundingBoxForStroke(stroke: BrushStroke, aspectRatio: number): [vec2, vec2] {
	const [xMinMax, yMinMax] = getBounds(stroke.points)
	const size = stroke.size // + stroke.feather * stroke.size
	const halfWidth = aspectRatio > 1 ? size * 0.5 : size * 0.5 * aspectRatio
	const halfHeight = aspectRatio > 1 ? (size * 0.5) / aspectRatio : size * 0.5
	return [
		[xMinMax[0] - halfWidth, xMinMax[1] + halfWidth],
		[yMinMax[0] - halfHeight, yMinMax[1] + halfHeight],
	]
}

export const calcBoundingBox = (
	strokes: BrushStroke[],
	aspectRatio: number /*height/width*/,
): Rectangle => {
	const bounding: vec2[] = []
	for (const stroke of strokes) {
		const [x, y] = calcBoundingBoxForStroke(stroke, aspectRatio)
		bounding.push([x[0], y[0]])
		bounding.push([x[1], y[1]])
	}
	const result = getRectBounds(bounding)
	return {
		x: result[0][0],
		y: result[1][0],
		width: result[0][1] - result[0][0],
		height: result[1][1] - result[1][0],
	}
}

const healingSizeScale = 2.0

// For healing, the slider behaves nonlinear; also make sure cursor.radius === stroke.size
export const sliderValueToHealingSize = (value: number) => {
	const linearRange = 0.15
	if (value > linearRange) {
		return (value * value) / healingSizeScale
	} else {
		return (linearRange * value) / healingSizeScale
	}
}

export const healingSizeToSliderValue = (size: number) => {
	const linearRange = (0.15 * 0.15) / healingSizeScale
	if (size > linearRange) {
		return Math.sqrt(size * healingSizeScale)
	} else {
		return (size * 0.15) / linearRange // = size * brushSizeScale / 0.15
	}
}

export const getPaddedAndClampedRect = (rect: Rectangle, pad = 0.0025, clamp = true): Rectangle => {
	const result = rectPadding(rect, pad)

	if (clamp) return rectClamp(result, 0.0, 1.0)
	else return result
}

export function getCenterOfStrokes(strokeCopy: BrushStroke[], rectCenter: vec2): vec2 | null {
	const points = strokeCopy[strokeCopy.length - 1]?.points ?? []
	const perfectPoint = vec2.fromValues(Infinity, Infinity)
	let distance = Infinity
	for (let i = 0; i < points.length; i += 2) {
		const x = points[i]
		const y = points[i + 1]
		const point = vec2.fromValues(x ?? -1, y ?? -1)
		if (isVec2InRange(point, 0, 1)) {
			const newDistance = vec2.distance(point, rectCenter)
			if (newDistance < distance) {
				perfectPoint[0] = point[0]
				perfectPoint[1] = point[1]
				distance = newDistance
			}
		}
	}

	return perfectPoint
}

export function getClampedRects(options: {
	strokesRect: Rectangle
	sourceCenter: vec2
	destinationCenter: vec2
}) {
	const { strokesRect, sourceCenter, destinationCenter } = options

	// recompute clamped destinationRect from strokes because it could move out/in of image bounds
	const destinationRect = getPaddedAndClampedRect(strokesRect)

	// sourceRect may update when destinationRect is clamped
	const dx = sourceCenter[0] - destinationCenter[0]
	const dy = sourceCenter[1] - destinationCenter[1]
	const sourceRect = rectOffset(destinationRect, dx, dy)

	// If source moves out of image bounds, need update destination rect as well
	return getUpdatedRectsByClampingSource(sourceRect, destinationRect)
}

export function getUpdatedRectsByClampingSource(sourceRect: Rectangle, destinationRect: Rectangle) {
	const sourceRectClamped = rectClamp(sourceRect, 0.0, 1.0)
	if (!rectEquals(sourceRect, sourceRectClamped)) {
		const dx1 = sourceRectClamped.x - sourceRect.x
		const dy1 = sourceRectClamped.y - sourceRect.y

		const newDestinationRect = {
			x: destinationRect.x + dx1,
			y: destinationRect.y + dy1,
			width: sourceRectClamped.width,
			height: sourceRectClamped.height,
		}

		return {
			sourceRect: sourceRectClamped,
			destinationRect: newDestinationRect,
		}
	}

	return { sourceRect, destinationRect }
}
